'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FooterSectionProps {
  className?: string;
}

const FooterSection = ({ className = '' }: FooterSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleNewsletterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      alert(`Thank you for subscribing with ${email}!`);
      setEmail('');
    }
  };

  const footerLinks = {
    product: [
      { label: 'How It Works', href: '/how-it-works' },
      { label: 'Product Showcase', href: '/product-showcase' },
      { label: 'Pricing Plans', href: '/pricing-plans' },
      { label: 'Order Your Card', href: '/get-started' }
    ],
    support: [
      { label: 'Support Hub', href: '/support-hub' },
      { label: 'FAQs', href: '/support-hub' },
      { label: 'Contact Us', href: '/support-hub' },
      { label: 'Track Order', href: '/support-hub' }
    ],
    company: [
      { label: 'About Us', href: '/homepage' },
      // { label: 'Careers', href: '/homepage' },
      // { label: 'Press Kit', href: '/homepage' },
      // { label: 'Partners', href: '/homepage' }
    ],
    legal: [
      { label: 'Privacy Policy', href: '/homepage' },
      { label: 'Terms of Service', href: '/homepage' },
      { label: 'Cookie Policy', href: '/homepage' },
      { label: 'Refund Policy', href: '/homepage' }
    ]
  };

  const socialLinks = [
    { name: 'Facebook', icon: 'ShareIcon', href: '#' },
    { name: 'Twitter', icon: 'ChatBubbleLeftIcon', href: '#' },
    { name: 'Instagram', icon: 'PhotoIcon', href: '#' },
    { name: 'LinkedIn', icon: 'BriefcaseIcon', href: '#' }
  ];

  const currentYear = isHydrated ? new Date().getFullYear() : 2026;

  return (
    <footer className={`bg-[#050B14] text-white ${className}`}>
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          <div className="lg:col-span-2">
            <Link href="/homepage" className="flex items-center space-x-2 mb-4">
              <div className="flex items-center justify-center w-10 h-10 bg-accent rounded-lg">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-white"
                >
                  <path
                    d="M12 2L2 7L12 12L22 7L12 2Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 17L12 22L22 17"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 12L12 17L22 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="text-xl font-heading font-bold">ParkPing</span>
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed">
              ParkPing is a privacy-first parking contact system that helps people reach vehicle owners without sharing personal phone numbers.
            </p>
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 bg-white/10 border border-white/10 rounded-lg flex items-center justify-center hover:bg-accent hover:text-accent-foreground transition-colors duration-300 text-white/70"
                  aria-label={social.name}
                >
                  <Icon name={social.icon as any} size={20} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-heading font-bold mb-4 text-white">Product</h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-white/60 hover:text-white transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-heading font-bold mb-4 text-white">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-white/60 hover:text-white transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-heading font-bold mb-4 text-white">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-white/60 hover:text-white transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-heading font-bold mb-4 text-white">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-white/60 hover:text-white transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-lg font-heading font-bold mb-4 text-white">Product Updates</h3>
              <form onSubmit={handleNewsletterSubmit} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 bg-white/10 text-white border border-white/10 rounded-xl focus:outline-none focus:border-accent/40 focus:ring-1 focus:ring-accent/20 transition-all"
                  required
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-accent text-accent-foreground font-heading font-semibold rounded-xl shadow-cta hover:shadow-cta-hover transition-all duration-300"
                >
                  Get Updates
                </button>
              </form>
            </div>

            <div className="text-center md:text-right">
              <p className="text-gray-500 text-sm">
                (c) {currentYear} ParkPing. All rights reserved.
              </p>
              <p className="text-gray-600 text-xs mt-2">
                Built in India.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;


